import type { MonsterEntity } from "../types/entity.js";
import type { Item, ItemStack } from "../types/items.js";
import type { LootPile } from "./LootPile.js";
import { resolveLootTable } from "./lootTable.js";
import { LootTableRegistry } from "./LootTableRegistry.js";
import { GOLD_ITEM_ID, makeGoldItem } from "./goldItem.js";

/**
 * E12/I19 — the runtime loot resolver handed to `CombatManager.setLootResolver`.
 *
 * On enemy death the manager calls this synchronously, so item records
 * can't be fetched on demand — callers preload every id their registered
 * loot tables can drop (see `LootTableRegistry.allItemIds`) and pass a
 * lookup into the cache. Unknown ids are skipped with a console warning
 * rather than blocking the drop.
 */
export function createLootTableResolver(
  getItem: (itemId: string) => Item | undefined,
): (entity: MonsterEntity) => LootPile | null {
  return (entity) => {
    if (!entity.lootTableId) return null;
    const table = LootTableRegistry.get(entity.lootTableId);
    if (!table) {
      console.warn(`[bk] enemy "${entity.dataId}" names unknown loot table "${entity.lootTableId}"`);
      return null;
    }
    const resolved = resolveLootTable(table, LootTableRegistry.lookup);
    const stacks: ItemStack[] = [];
    for (const drop of resolved) {
      const item = drop.itemId === GOLD_ITEM_ID ? makeGoldItem() : getItem(drop.itemId);
      if (!item) {
        console.warn(
          `[bk] loot table "${table.id}" drops unknown item "${drop.itemId}" — skipped`,
        );
        continue;
      }
      stacks.push({ item, quantity: drop.quantity });
    }
    if (stacks.length === 0) return null;
    return {
      id: `pile-${entity.instanceId}`,
      position: { x: entity.position.x, z: entity.position.z },
      stacks,
    };
  };
}
